import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation } from "@react-navigation/native";
import {
  FertilityTestingStackScreenProps,
  type FertilityTestingStackParamList,
} from "../FertilityTestingNavigator";
import {
  QuestionLayout,
  SquareOptionButton,
} from "@src/packages/medical-history/components";
import { Dna, TestTube2, Sparkles, type LucideIcon } from "lucide-react-native";
import { useTranslations } from "@src/packages/i18n";
import { useFertilityTesting } from "../FertilityTestingContext";
import { getNextTestScreen } from "../getNextTestScreen";
import { useFertilityTestingUpload } from "../useFertilityTestingUpload";

type GeneticTestType =
  | "karyotyping"
  | "carrier_testing"
  | "exome_genome"
  | "other";

const geneticTests: {
  id: GeneticTestType;
  labelKey: string;
  icon: LucideIcon;
}[] = [
  {
    id: "karyotyping",
    labelKey: "med_history.fertility.testing.genetic_type.karyotyping",
    icon: Dna,
  },
  {
    id: "carrier_testing",
    labelKey: "med_history.fertility.testing.genetic_type.carrier_testing",
    icon: TestTube2,
  },
  {
    id: "exome_genome",
    labelKey: "med_history.fertility.testing.genetic_type.exome_genome",
    icon: Dna,
  },
  {
    id: "other",
    labelKey: "med_history.fertility.testing.genetic_type.other",
    icon: Sparkles,
  },
];

export function GeneticTestsScreen() {
  const t = useTranslations();
  const { state, updateState } = useFertilityTesting();
  const navigation =
    useNavigation<
      FertilityTestingStackScreenProps<"GeneticTests">["navigation"]
    >();
  const { uploadAction, uploadModal, uploadedFiles } =
    useFertilityTestingUpload();

  // Find existing genetic test results in state
  const geneticResults = state.testResults.filter(
    (tr) => tr.type === "genetics",
  );

  // Map API category to UI type
  const categoryToUI = useMemo<Record<string, GeneticTestType>>(
    () => ({
      karyotype: "karyotyping",
      carrier_testing: "carrier_testing",
      sequencing: "exome_genome",
      other: "other",
    }),
    [],
  );

  const selected = useMemo<Set<GeneticTestType>>(() => {
    const set = new Set<GeneticTestType>();
    geneticResults.forEach((result) => {
      if (result.geneticsTestCategory) {
        const uiType = categoryToUI[result.geneticsTestCategory];
        if (uiType) set.add(uiType);
      }
    });
    return set;
  }, [geneticResults, categoryToUI]);

  const handleSelectionChange = (newSelection: GeneticTestType[]) => {
    const categoryMap: Record<GeneticTestType, string> = {
      karyotyping: "karyotype",
      carrier_testing: "carrier_testing",
      exome_genome: "sequencing",
      other: "other",
    };

    const oldSelection = new Set<GeneticTestType>();
    geneticResults.forEach((result) => {
      if (result.geneticsTestCategory) {
        const uiType = categoryToUI[result.geneticsTestCategory];
        if (uiType) oldSelection.add(uiType);
      }
    });

    const newSelectionSet = new Set(newSelection);

    // Remove deselected items
    let updatedResults = state.testResults.filter((tr) => {
      if (tr.type !== "genetics") return true;
      const uiType = categoryToUI[tr.geneticsTestCategory || ""];
      return uiType && newSelectionSet.has(uiType as GeneticTestType);
    });

    // Add newly selected items
    newSelection.forEach((uiType) => {
      if (!oldSelection.has(uiType)) {
        const newTestResult: import("@louise/medical-history/basic-info/types").TestResultCreate =
          {
            type: "genetics" as const,
            geneticsTestCategory: categoryMap[
              uiType
            ] as import("@louise/medical-history/basic-info/types").GeneticsTestCategory,
          };
        updatedResults = [...updatedResults, newTestResult];
      }
    });

    updateState({ testResults: updatedResults });
  };

  const handleNext = () => {
    // Increment navigation index and get next screen
    const newIndex = (state.currentNavigationIndex || 0) + 1;
    updateState({ currentNavigationIndex: newIndex });
    const nextScreen = getNextTestScreen({
      ...state,
      currentNavigationIndex: newIndex,
    });
    navigation.navigate(nextScreen as keyof FertilityTestingStackParamList);
  };

  const handleToggle = (id: GeneticTestType) => {
    const current = new Set(selected);
    if (current.has(id)) {
      current.delete(id);
    } else {
      current.add(id);
    }
    handleSelectionChange(Array.from(current));
  };

  return (
    <>
      <QuestionLayout
        title={t("med_history.fertility.testing.genetic_title")}
        subtitle={t("med_history.fertility.testing.genetic_subtitle")}
        onBack={() => navigation.goBack()}
        onSave={handleNext}
        saveDisabled={selected.size === 0}
        saveLabel={t("med_history.fertility.continue")}
        secondaryAction={uploadAction}
        uploadedFiles={uploadedFiles}
      >
        <View style={styles.optionsGrid}>
          {geneticTests.map((test) => {
            const isSelected = selected.has(test.id);
            return (
              <View key={test.id} style={styles.optionWrapper}>
                <SquareOptionButton
                  icon={test.icon}
                  label={t(test.labelKey)}
                  isSelected={isSelected}
                  onPress={() => handleToggle(test.id)}
                  iconSize={20}
                  containerSize={40}
                  labelSize="sm"
                />
              </View>
            );
          })}
        </View>
      </QuestionLayout>
      {uploadModal}
    </>
  );
}

const styles = StyleSheet.create({
  optionsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 40,
    width: "100%",
  },
  optionWrapper: {
    width: "47%",
    minWidth: "47%",
  },
});

export default GeneticTestsScreen;
